import { cn } from "@wellcheck/components/lib/cn";

interface WizardProgressProps {
  steps: ReadonlyArray<{ label: string }>;
  currentIndex: number; // 0-origin
  // 渡したステップにバッジクリックでジャンプ可能になる。未指定の step はクリック不可
  onStepClick?: (index: number) => void;
  isStepNavigable?: (index: number) => boolean;
}

/**
 * WizardProgress — Wizard 上部の段階インジケータ。
 *
 * 罫線と数字バッジで「完了 / 現在 / 未着手」を区別する。
 * onStepClick が渡されれば、isStepNavigable が true を返す step のバッジを button 化する。
 */
export function WizardProgress({
  steps,
  currentIndex,
  onStepClick,
  isStepNavigable,
}: WizardProgressProps) {
  return (
    <ol className="flex items-center gap-3 text-sm" aria-label="進捗">
      {steps.map((step, i) => {
        const isCurrent = i === currentIndex;
        const isDone = i < currentIndex;
        const navigable = !!onStepClick && !isCurrent && (isStepNavigable?.(i) ?? false);
        const badgeClass = cn(
          "flex shrink-0 items-center justify-center rounded-full tabular-nums transition-all",
          isCurrent
            ? "h-9 w-9 bg-primary text-base font-semibold text-primary-foreground shadow-md ring-4 ring-primary/20"
            : "h-7 w-7 text-sm",
          isDone && !isCurrent && "bg-primary/80 text-primary-foreground",
          !isCurrent && !isDone && "border border-border bg-background text-muted-foreground",
          navigable && "cursor-pointer hover:opacity-80",
        );
        return (
          <li key={i} className="flex flex-1 items-center gap-3 min-w-0">
            {navigable ? (
              <button
                type="button"
                className={badgeClass}
                onClick={() => onStepClick?.(i)}
                aria-label={`step ${i + 1} へ移動`}
              >
                {i + 1}
              </button>
            ) : (
              <span className={badgeClass} aria-current={isCurrent ? "step" : undefined}>
                {i + 1}
              </span>
            )}
            {step.label && (
              <span
                className={cn(
                  "truncate",
                  isCurrent ? "font-medium text-foreground" : "text-muted-foreground",
                )}
              >
                {step.label}
              </span>
            )}
            {i < steps.length - 1 && (
              <span
                className={cn("ml-1 h-px flex-1", isDone ? "bg-primary/60" : "bg-border")}
                aria-hidden
              />
            )}
          </li>
        );
      })}
    </ol>
  );
}
